import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, TextField, Typography, Alert, Grid, Paper } from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';
import { useGoogleMaps } from '../providers/GoogleMapsProvider';
import { updateQuota, isMapsAvailable } from '../utils/googleMapsQuota';
import { extractRegionalInfo } from '../utils/regionalMatcher';

/**
 * GoogleMapsAddressInput Component
 * 
 * This component uses the new PlaceAutocompleteElement (March 2025) instead of 
 * the deprecated google.maps.places.Autocomplete to provide address autocomplete
 * functionality for Australian addresses.
 * 
 * Migration notes:
 * - Replaced google.maps.places.Autocomplete with google.maps.places.PlaceAutocompleteElement
 * - Updated event listener from 'place_changed' to 'gmp-select'
 * - Changed componentRestrictions to includedRegionCodes
 * - Changed types to includedPrimaryTypes
 * - Uses google.maps.importLibrary('places') for proper API loading
 */

const GoogleMapsAddressInput = ({ 
  value, 
  onChange, 
  onCoordinateChange,
  initialAddress = ''
}) => {
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState({ lat: -25.2744, lng: 133.7751 }); // Australia center
  const [zoom, setZoom] = useState(4);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [apiError, setApiError] = useState(null);
  
  // Individual address field states
  const [addressFields, setAddressFields] = useState({
    streetNumber: '',
    street: '',
    suburb: '',
    state: '',
    postcode: '',
    country: ''
  });
  
  const [formattedAddress, setFormattedAddress] = useState(initialAddress || '');
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteElementRef = useRef(null);
  const autocompleteContainerRef = useRef(null);
  const initialAddressSet = useRef(false);
  const isComponentMounted = useRef(true);

  // Use Google Maps context
  const { isLoaded, loadError } = useGoogleMaps();

  // Function to create or update marker
  const createOrUpdateMarker = useCallback((position) => {
    const currentMap = mapRef.current;
    if (window.google && window.google.maps && currentMap) {
      try {
        // Remove existing marker if it exists
        if (markerRef.current) {
          markerRef.current.map = null;
          markerRef.current = null;
        }
        
        // Create new marker
        if (window.google.maps.marker && window.google.maps.marker.AdvancedMarkerElement) {
          markerRef.current = new window.google.maps.marker.AdvancedMarkerElement({
            map: currentMap,
            position: position,
            title: 'Selected Location'
          });
        }
      } catch (error) {
        console.error('Error creating AdvancedMarkerElement:', error);
      }
    }
  }, []); // Remove map dependency to prevent re-renders

  // Check quota on component mount
  useEffect(() => {
    const available = isMapsAvailable();
    setQuotaExceeded(!available);
  }, []);

  // Handle API loading errors
  useEffect(() => {
    if (loadError) {
      console.error('Google Maps API failed to load:', loadError);
      setApiError('Google Maps failed to load. Please check your internet connection and try again.');
      
      // Check if it's a billing error
      if (loadError.message && loadError.message.includes('billing')) {
        setApiError('Google Maps billing not enabled. Please enable billing in Google Cloud Console.');
      }
    }
  }, [loadError]);

  // Function to parse Australian address
  const parseAustralianAddress = useCallback((addressComponents, formattedAddr = '') => {
    let streetNumber = '';
    let street = '';
    let suburb = '';
    let state = '';
    let postcode = '';
    let country = '';
    
    // Map for component types
    const componentMap = {
      street_number: 'short_name',
      route: 'long_name', // street name
      locality: 'long_name', // suburb
      administrative_area_level_1: 'short_name', // state
      postal_code: 'short_name', // postcode
      country: 'long_name' // country
    };
    
    // Check if addressComponents is valid
    if (!addressComponents || !Array.isArray(addressComponents)) {
      return {
        streetNumber: '',
        street: '',
        suburb: '',
        state: '',
        postcode: '',
        country: '',
        streetAddress: '',
        region: ''
      };
    }
    
    // Extract address components
    for (const component of addressComponents) {
      if (!component || !component.types) {
        continue;
      }
      
      for (const type of component.types) {
        if (componentMap[type]) {
          // Try different property names that might exist in the new API
          const shortName = component.shortText || component.short_name;
          const longName = component.longText || component.long_name;
          const value = componentMap[type] === 'short_name' ? shortName : longName;
          
          if (type === 'street_number') {
            streetNumber = value;
          } else if (type === 'route') {
            street = value;
          } else if (type === 'locality') {
            suburb = value;
          } else if (type === 'administrative_area_level_1') {
            state = value;
          } else if (type === 'postal_code') {
            postcode = value;
          } else if (type === 'country') {
            country = value;
          }
        }
      }
    }
    
    // Update the address fields state
    const newAddressFields = {
      streetNumber,
      street,
      suburb,
      state,
      postcode,
      country
    };
    
    setAddressFields(newAddressFields);
    setFormattedAddress(formattedAddr);
    
    // Extract regional information (State, Greater Region, Sub-Region)
    const regionalInfo = extractRegionalInfo(addressComponents);
    
    const result = {
      streetAddress: `${streetNumber} ${street}`.trim(),
      state: regionalInfo?.state || state,
      suburb,
      postcode,
      country,
      region: '', // Deprecated field
      greaterRegion: regionalInfo?.greaterRegion || null,
      subRegion: regionalInfo?.subRegion || null,
      lgaName: regionalInfo?.lgaName || null
    };
    
    return result;
  }, []);

  // Handle map load
  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapRef.current = mapInstance; // Store map reference
    
    // Track map load in quota
    try {
      const quota = updateQuota('maps');
      if (quota.disabled) {
        setQuotaExceeded(true);
        console.warn('Maps quota exceeded');
        return;
      }
    } catch (error) {
      console.error('Error updating maps quota:', error);
    }
  }, []);

  // Handle place selection from autocomplete element
  const handlePlaceSelect = useCallback(async (event) => {
    if (!isComponentMounted.current) return;
    const placePrediction = event.placePrediction;

    // Track Places API usage
    try {
      updateQuota('places');
    } catch (error) {
      console.error('Error updating places quota:', error);
    }
    
    try {
      // Convert prediction to place and fetch required fields
      const place = placePrediction.toPlace();
      
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'location', 'displayName']
      });
      
      if (place.location) {
        const lat = place.location.lat();
        const lng = place.location.lng();
        const newCenter = { lat, lng };
        
        // Update state
        setCenter(newCenter);
        setZoom(14);
        
        // Update map
        const currentMap = mapRef.current;
        if (currentMap) {
          currentMap.panTo(newCenter);
          currentMap.setZoom(14);
        }
        
        // Update marker
        createOrUpdateMarker(newCenter);
        
        // Parse and send address data
        const fullAddress = place.formattedAddress || place.displayName || '';
        const parsedAddress = parseAustralianAddress(place.addressComponents || [], fullAddress);
        
        const addressData = {
          fullAddress,
          ...parsedAddress,
          coordinates: { lat, lng }
        };
        
        if (onChange) onChange(fullAddress);
        if (onCoordinateChange) onCoordinateChange(addressData);
      }
    } catch (error) {
      console.error('Error handling place selection:', error);
    }
  }, [createOrUpdateMarker, onChange, onCoordinateChange, parseAustralianAddress]);

  // Handle autocomplete errors
  const handlePlaceError = useCallback((event) => {
    console.error('Autocomplete error:', event);
  }, []);

  // Handle manual address field changes
  const handleAddressFieldChange = useCallback((field, value) => {
    const newAddressFields = { ...addressFields, [field]: value };
    setAddressFields(newAddressFields);
    
    // Reconstruct full address from individual fields
    const parts = [
      newAddressFields.streetNumber,
      newAddressFields.street,
      newAddressFields.suburb,
      newAddressFields.state,
      newAddressFields.postcode,
      newAddressFields.country
    ].filter(part => part && part.trim() !== '');
    
    const reconstructedAddress = parts.join(', ');
    setFormattedAddress(reconstructedAddress);
    
    // Create address data object for parent component
    const addressData = {
      fullAddress: reconstructedAddress,
      streetAddress: `${newAddressFields.streetNumber} ${newAddressFields.street}`.trim(),
      suburb: newAddressFields.suburb,
      state: newAddressFields.state,
      postcode: newAddressFields.postcode,
      country: newAddressFields.country,
      region: '',
      coordinates: { lat: center.lat, lng: center.lng }
    };
    
    if (onChange) onChange(reconstructedAddress);
    if (onCoordinateChange) onCoordinateChange(addressData);
  }, [addressFields, center, onChange, onCoordinateChange]);

  // Cleanup function
  const onUnmount = useCallback(() => {
    if (markerRef.current) {
      markerRef.current.map = null;
      markerRef.current = null;
    }
    
    // Clear map reference
    mapRef.current = null;
    
    // Properly clean up autocomplete element
    if (autocompleteElementRef.current) {
      try {
        // Remove event listeners
        autocompleteElementRef.current.removeEventListener('gmp-select', handlePlaceSelect);
        autocompleteElementRef.current.removeEventListener('gmp-error', handlePlaceError);
        
        // Remove from DOM if still attached
        if (autocompleteElementRef.current.parentNode) {
          autocompleteElementRef.current.parentNode.removeChild(autocompleteElementRef.current);
        }
        
        autocompleteElementRef.current = null;
      } catch (error) {
        console.error('Error cleaning up autocomplete element:', error);
        autocompleteElementRef.current = null;
      }
    }
  }, [handlePlaceSelect, handlePlaceError]);

  // Set up autocomplete and marker when map loads
  useEffect(() => {
    if (!isLoaded || !map) return;
    
    // Add a small delay to ensure DOM is fully ready
    const setupTimeout = setTimeout(() => {
    
    // Create initial marker
    createOrUpdateMarker(center);
    
    // Autocomplete is now handled in a separate useEffect
    
    // Set up map click handler
    if (map) {
      map.addListener('click', async (e) => {
        const lat = e.latLng.lat();
        const lng = e.latLng.lng();
        
        // Update center and marker
        const newCenter = { lat, lng };
        setCenter(newCenter);
        
        // Create or update marker at clicked position
        createOrUpdateMarker(newCenter);
        
        // Track geocoding usage in quota
        try {
          const quota = updateQuota('geocoding');
          if (quota.disabled) {
            setQuotaExceeded(true);
            console.warn('Geocoding quota exceeded');
            return;
          }
        } catch (error) {
          console.error('Error updating geocoding quota:', error);
        }
        
        // Reverse geocode to get address
        try {
          const geocoder = new window.google.maps.Geocoder();
          const results = await new Promise((resolve, reject) => {
            geocoder.geocode({ location: { lat, lng } }, (results, status) => {
              if (status === 'OK') resolve(results);
              else reject(new Error(`Geocoding failed: ${status}`));
            });
          });
          
          if (results && results.length > 0) {
            const place = results[0];
            const parsedAddress = parseAustralianAddress(place.address_components || []);
            const fullAddress = place.formatted_address || '';
            
            // Create address data object
            const addressData = {
              fullAddress,
              ...parsedAddress,
              coordinates: { lat, lng }
            };
            
            // Update parent component
            if (onChange) {
              onChange(fullAddress);
            }
            if (onCoordinateChange) {
              onCoordinateChange(addressData);
            }
          }
        } catch (error) {
          console.error('Error geocoding location:', error);
        }
      });
    }
    
    }, 100); // 100ms delay
    
    return () => {
      clearTimeout(setupTimeout);
    };
  }, [isLoaded, map]); // Simplified dependencies

  // Initialize with initial address if provided
  useEffect(() => {
    if (isLoaded && initialAddress && !initialAddressSet.current && map) {
      initialAddressSet.current = true;
      
      // Track geocoding usage for initial address
      try {
        const quota = updateQuota('geocoding');
        if (quota.disabled) {
          setQuotaExceeded(true);
          console.warn('Geocoding quota exceeded, cannot load initial address');
          return;
        }
      } catch (error) {
        console.error('Error updating geocoding quota:', error);
      }
      
      // Geocode the initial address
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: initialAddress }, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const place = results[0];
          const location = place.geometry.location;
          
          // Set center and zoom
          setCenter({ 
            lat: location.lat(), 
            lng: location.lng() 
          });
          setZoom(14);
          
          // Create or update marker at initial address location
          createOrUpdateMarker({ 
                lat: location.lat(),
                lng: location.lng()
              });
          
          // Parse address components for future reference
          const parsedAddress = parseAustralianAddress(place.address_components || []);
        }
      });
    }
  }, [isLoaded, initialAddress, map]); // Simplified dependencies

  // Component mount/unmount tracking
  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);

  // Update formatted address when initialAddress changes
  useEffect(() => {
    if (initialAddress && !formattedAddress) {
      setFormattedAddress(initialAddress);
    }
  }, [initialAddress, formattedAddress]);

  // Setup PlaceAutocompleteElement
  useEffect(() => {
    if (!isLoaded || !autocompleteContainerRef.current) return;
    
    let setupTimeout;
    
    const setupPlaceAutocompleteElement = async () => {
      // Don't setup if component is unmounted
      if (!isComponentMounted.current) return;
      
      // Check if autocomplete element already exists
      if (autocompleteElementRef.current) {
        return true;
      }
      
      try {
        // Import the Places library to get PlaceAutocompleteElement
        const { PlaceAutocompleteElement } = await window.google.maps.importLibrary('places');
        
        // Create new PlaceAutocompleteElement
        const placeAutocomplete = new PlaceAutocompleteElement({
          includedRegionCodes: ['AU'], // Replaced componentRestrictions
          includedPrimaryTypes: ['geocode'] // Use 'geocode' for addresses (not 'address')
          // Removed locationRestriction to allow broader address suggestions
        });
        
        // Set up styling
        placeAutocomplete.style.width = '100%';
        placeAutocomplete.style.fontSize = '16px';
        placeAutocomplete.style.padding = '16.5px 14px';
        placeAutocomplete.style.border = '1px solid rgba(0, 0, 0, 0.23)';
        placeAutocomplete.style.borderRadius = '4px';
        placeAutocomplete.style.fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
        
        // Store reference immediately
        autocompleteElementRef.current = placeAutocomplete;
        
        // Add event listeners
        placeAutocomplete.addEventListener('gmp-select', handlePlaceSelect);
        placeAutocomplete.addEventListener('gmp-error', handlePlaceError);
        
        // Add to DOM
        const container = autocompleteContainerRef.current;
        container.innerHTML = ''; // Clear any existing content
        container.appendChild(placeAutocomplete);

        // Populate the input with the initial address if one is available.
        // The PlaceAutocompleteElement renders its internal <input> asynchronously
        // inside a shadow root, so we need a short delay before setting the value.
        const addrToSet = initialAddress || formattedAddress;
        if (addrToSet) {
          setTimeout(() => {
            const el = autocompleteElementRef.current;
            if (!el) return;
            try {
              el.value = addrToSet;
            } catch (_) {
              // Fallback: write directly to the shadow-DOM input
              const inp = el.shadowRoot?.querySelector('input');
              if (inp) inp.value = addrToSet;
            }
          }, 200);
        }
        
        return true;
        
      } catch (error) {
        console.error('Error setting up PlaceAutocompleteElement:', error);
        return false;
      }
    };
    
    // Try initial setup with small delay
    setupTimeout = setTimeout(() => {
      setupPlaceAutocompleteElement();
    }, 100);
    
    // Cleanup
    return () => {
      if (setupTimeout) clearTimeout(setupTimeout);
      
      // Clean up autocomplete element completely on unmount
      if (autocompleteElementRef.current) {
        try {
          autocompleteElementRef.current.removeEventListener('gmp-select', handlePlaceSelect);
          autocompleteElementRef.current.removeEventListener('gmp-error', handlePlaceError);
          
          if (autocompleteElementRef.current.parentNode) {
            autocompleteElementRef.current.parentNode.removeChild(autocompleteElementRef.current);
          }
        } catch (e) {
          console.warn('Error cleaning up PlaceAutocompleteElement on unmount:', e);
        }
        autocompleteElementRef.current = null;
      }
    };
  }, [isLoaded, map, handlePlaceSelect, handlePlaceError]);

  // Keep the autocomplete input text in sync when initialAddress changes after the
  // element is already mounted (e.g. parent passes a new value, or the tab is
  // revisited after the element has been set up in a previous render cycle).
  useEffect(() => {
    if (!autocompleteElementRef.current || !initialAddress) return;
    try {
      autocompleteElementRef.current.value = initialAddress;
    } catch (_) {
      const inp = autocompleteElementRef.current.shadowRoot?.querySelector('input');
      if (inp) inp.value = initialAddress;
    }
  }, [initialAddress]);

  // Show error states
  if (loadError || apiError) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Google Maps Unavailable
          </Typography>
          <Typography variant="body2">
            {apiError || 'Google Maps failed to load. Please try again later.'}
          </Typography>
        </Alert>
        
        {/* Fallback to manual address entry when Google Maps is unavailable */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Manual Address Entry
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Number"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.streetNumber}
              onChange={(e) => handleAddressFieldChange('streetNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label="Street"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.street}
              onChange={(e) => handleAddressFieldChange('street', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Suburb"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.suburb}
              onChange={(e) => handleAddressFieldChange('suburb', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="State"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.state}
              onChange={(e) => handleAddressFieldChange('state', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Postcode"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.postcode}
              onChange={(e) => handleAddressFieldChange('postcode', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Country"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.country}
              onChange={(e) => handleAddressFieldChange('country', e.target.value)}
            />
          </Grid>
        </Grid>
        
        {/* Address Display for manual entry */}
        {formattedAddress && (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Address:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formattedAddress}
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  if (quotaExceeded) {
    return (
      <Box sx={{ width: '100%' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Google Maps Quota Exceeded
          </Typography>
          <Typography variant="body2">
            The Google Maps quota has been exceeded. Please use manual address entry.
          </Typography>
        </Alert>
        
        {/* Fallback to manual address entry when quota is exceeded */}
        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
          Manual Address Entry
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Number"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.streetNumber}
              onChange={(e) => handleAddressFieldChange('streetNumber', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={9}>
            <TextField
              label="Street"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.street}
              onChange={(e) => handleAddressFieldChange('street', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Suburb"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.suburb}
              onChange={(e) => handleAddressFieldChange('suburb', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="State"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.state}
              onChange={(e) => handleAddressFieldChange('state', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label="Postcode"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.postcode}
              onChange={(e) => handleAddressFieldChange('postcode', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Country"
              fullWidth
              variant="outlined"
              size="small"
              value={addressFields.country}
              onChange={(e) => handleAddressFieldChange('country', e.target.value)}
            />
          </Grid>
        </Grid>
        
        {/* Address Display for manual entry */}
        {formattedAddress && (
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              backgroundColor: '#f5f5f5',
              border: '1px solid #e0e0e0'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Address:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formattedAddress}
            </Typography>
          </Paper>
        )}
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          Loading Google Maps...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        Search for an address or click on the map
      </Typography>
      
      {/* PlaceAutocompleteElement Container */}
      <Box
        ref={autocompleteContainerRef}
        sx={{ 
          mb: 2,
          '& gmp-place-autocomplete': {
            width: '100%',
            display: 'block'
          }
        }}
      />
      
      {/* Helper text */}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
        Start typing and select from the dropdown suggestions
      </Typography>
      
      {/* Map Container */}
      <Box 
        sx={{ 
          height: 300,
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid #ddd',
          mb: 3
        }}
        ref={mapRef}
      >
        <GoogleMap
          mapContainerStyle={{
            width: '100%',
            height: '100%'
          }}
          center={center}
          zoom={zoom}
          onLoad={onLoad}
          onUnmount={onUnmount}
          options={{
            fullscreenControl: false,
            mapTypeControl: false,
            streetViewControl: false,
            zoomControl: true,
            mapId: 'austnews-crm-map' // Required for Advanced Markers
          }}
        />
      </Box>
      
      {/* Address Display */}
      {formattedAddress && (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 2, 
            backgroundColor: '#f5f5f5',
            border: '1px solid #e0e0e0'
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Address:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {formattedAddress}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default GoogleMapsAddressInput; 